/* ==========================================================================
   VI Knowledge Hub — engine for the wine knowledge bank
   --------------------------------------------------------------------------
   Powers BOTH knowledge pages (Vinfakta & Myter, Druvor & Vinstilar). It is
   fully data-driven: each section embeds a JSON config and the engine
   auto-initialises every [data-vi-knowledge] root on the page.

   Responsibilities: fetch lightweight indexes, debounced search, category
   filtering, sorting, capped/lazy rendering, on-demand full-content load,
   safe markdown rendering, an accessible reader modal, and graceful empty /
   loading / error (admin) states.

   No external libraries. No Shopify writes. No network calls other than
   GET requests for the theme's own JSON assets.
   ========================================================================== */
(function () {
  'use strict';

  var NS = 'vi-knowledge';

  // ── Default UI strings (Swedish). Merged with per-section config.labels. ──
  var DEFAULT_LABELS = {
    all: 'Alla',
    searchLabel: 'Sök i kunskapsbanken',
    searchPlaceholder: 'Sök på namn, ämne eller nyckelord…',
    sortLabel: 'Sortera',
    sortRelevance: 'Relevans',
    sortTitleAsc: 'Namn A–Ö',
    sortTitleDesc: 'Namn Ö–A',
    sortRelated: 'Mest relaterat',
    results: '{n} träffar',
    resultsOne: '1 träff',
    loadMore: 'Visa fler',
    readMore: 'Läs mer',
    related: 'Relaterat innehåll',
    sources: 'Källor',
    relatedCount: '{n} relaterade',
    loadingTitle: 'Laddar kunskapsbanken…',
    loadingText: 'Ett ögonblick.',
    emptyTitle: 'Inget innehåll matchar',
    emptyText: 'Prova en annan sökterm eller rensa filtren.',
    errorTitle: 'Innehållet kunde inte laddas',
    errorText: 'Innehållet kunde inte laddas just nu.',
    adminText: 'Adminnotis: kunskaps-JSON kunde inte hämtas. Kör "node scripts/build-knowledge-bundles.mjs" så de flata runtime-assetsen (assets/knowledge-*.json) byggs och committas. Se docs/knowledge-hub-runtime-fix-report.md.',
    readerLoading: 'Laddar artikel…',
    readerFallback: 'Full text kunde inte laddas. Visar sammanfattning.',
    verdict: { 'true': 'SANT', 'false': 'MYT', 'partly': 'DELVIS MYT' },
    glossaryTag: 'Ordlista',
    types: {
      'fact': 'Vinfakta',
      'myth': 'Myt',
      'region': 'Region',
      'glossary': 'Ordlista',
      'grape': 'Druva',
      'red-grape': 'Röd druva',
      'white-grape': 'Vit druva',
      'style': 'Vinstil',
      'sparkling': 'Mousserande',
      'dessert': 'Dessertvin',
      'fortified': 'Starkvin'
    }
  };

  // Category groups: a parent chip also matches its children. Wine-style
  // subcategories are a SEPARATE dimension (see STYLE_SUBCAT), not item
  // categories, so the "style" chip simply matches every vinstil.
  var CATEGORY_GROUPS = {
    'grape': ['grape', 'red-grape', 'white-grape'],
    'style': ['style']
  };

  // Wine-style subcategory chip key → the token expected in item.subcats
  // (built from the export's `subcategories` array). Lets the Druvor &
  // Vinstilar page filter vinstilar by subcategory without touching the
  // red/white DRUVA chips.
  var STYLE_SUBCAT = {
    'sparkling': 'mousserande', 'mousserande': 'mousserande',
    'dessert': 'dessertvin', 'dessertvin': 'dessertvin',
    'fortified': 'starkvin', 'starkvin': 'starkvin',
    'naturvin': 'naturvin',
    'roda-stilar': 'rott', 'vita-stilar': 'vitt',
    'rose': 'rose', 'orange': 'orange',
    'regional-stil': 'regional-stil'
  };

  // Synonyms → canonical category key. Keys are slugified before lookup.
  var CATEGORY_SYNONYMS = {
    'red-grape': ['red-grape', 'red', 'roda-druvor', 'rod-druva', 'roda', 'bla-druvor', 'blue-grape', 'rott'],
    'white-grape': ['white-grape', 'white', 'vita-druvor', 'vit-druva', 'vita'],
    'grape': ['grape', 'grapes', 'druva', 'druvor'],
    'style': ['style', 'styles', 'vinstil', 'vinstilar', 'wine-style'],
    'sparkling': ['sparkling', 'mousserande', 'bubbel'],
    'dessert': ['dessert', 'dessertvin', 'sweet', 'sott', 'sweet-wine'],
    'fortified': ['fortified', 'starkvin', 'fortified-wine'],
    'naturvin': ['naturvin', 'natural', 'natural-wine'],
    'roda-stilar': ['roda-stilar', 'roda-stil', 'red-styles'],
    'vita-stilar': ['vita-stilar', 'vita-stil', 'white-styles'],
    'rose': ['rose', 'rosevin', 'rose-stil'],
    'orange': ['orange', 'orangevin', 'amber', 'amber-wine'],
    'regional-stil': ['regional-stil', 'regional', 'regional-style'],
    'fact': ['fact', 'facts', 'vinfakta', 'fakta', 'wine-fact'],
    'myth': ['myth', 'myths', 'myt', 'myter'],
    'region': ['region', 'regions', 'regioner', 'omrade'],
    'glossary': ['glossary', 'ordlista', 'term', 'terms', 'begrepp']
  };

  var SYNONYM_LOOKUP = (function () {
    var map = {};
    for (var key in CATEGORY_SYNONYMS) {
      if (!Object.prototype.hasOwnProperty.call(CATEGORY_SYNONYMS, key)) continue;
      var list = CATEGORY_SYNONYMS[key];
      for (var i = 0; i < list.length; i++) map[list[i]] = key;
    }
    return map;
  })();

  // ── Small helpers ───────────────────────────────────────────────────────
  // Slug identity: lowercase + kebab, but PRESERVE non-ASCII letters so that
  // distinct Swedish slugs (vinordlista-a / -ä / -å / -ö) do not collide.
  function slugify(value) {
    return String(value == null ? '' : value).toLowerCase().trim()
      .replace(/[^a-z0-9\u0080-\uffff]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // ASCII-folded key, used only for accent-insensitive category matching.
  function asciiFold(value) {
    var s = String(value == null ? '' : value);
    if (typeof s.normalize === 'function') s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return s;
  }

  function normalizeCategory(value) {
    var s = asciiFold(value).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (!s) return '';
    return SYNONYM_LOOKUP[s] || s;
  }

  function normalizeVerdict(value) {
    var s = slugify(value);
    if (!s) return null;
    if (['true', 'sant', 'correct', 'verified', 'ja'].indexOf(s) >= 0) return 'true';
    if (['false', 'falskt', 'myth', 'myt', 'myte', 'busted', 'nej', 'falsk'].indexOf(s) >= 0) return 'false';
    if (['partly', 'partially', 'delvis', 'delvis-sant', 'delvis-myte', 'delvis-myt', 'mixed'].indexOf(s) >= 0) return 'partly';
    return null;
  }

  // Wine-style subcategory token: accent-folded slug (rött -> rott, rosé -> rose,
  // "regional stil" -> regional-stil) so it matches STYLE_SUBCAT targets.
  function normalizeSubcat(value) {
    return asciiFold(slugify(value)).toLowerCase();
  }

  function toArray(value) {
    if (Array.isArray(value)) return value.filter(function (v) { return v != null && v !== ''; });
    if (typeof value === 'string') return value.split(',').map(function (v) { return v.trim(); }).filter(Boolean);
    return [];
  }

  function firstString() {
    for (var i = 0; i < arguments.length; i++) {
      if (typeof arguments[i] === 'string' && arguments[i].trim() !== '') return arguments[i];
    }
    return '';
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function format(tpl, n) { return String(tpl).replace('{n}', n); }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function deepMerge(base, override) {
    var out = {};
    var k;
    for (k in base) if (Object.prototype.hasOwnProperty.call(base, k)) out[k] = base[k];
    if (override && typeof override === 'object') {
      for (k in override) {
        if (!Object.prototype.hasOwnProperty.call(override, k)) continue;
        if (override[k] && typeof override[k] === 'object' && !Array.isArray(override[k]) &&
          base[k] && typeof base[k] === 'object') {
          out[k] = deepMerge(base[k], override[k]);
        } else {
          out[k] = override[k];
        }
      }
    }
    return out;
  }

  // ── Safe markdown → HTML ─────────────────────────────────────────────────
  function safeUrl(url) {
    var u = String(url || '').trim();
    if (!u) return '';
    if (/^(https?:|mailto:|tel:)/i.test(u)) return u;     // explicit safe schemes
    if (/^(\/|#|\.\/|\.\.\/)/.test(u)) return u;          // relative / anchor
    if (!/:/.test(u)) return u;                            // scheme-less relative
    return '';                                             // drop anything else (e.g. script schemes)
  }

  function renderInline(text) {
    var out = escapeHtml(text);
    // inline code first so other markers inside are left alone
    out = out.replace(/`([^`]+)`/g, function (m, code) { return '<code>' + code + '</code>'; });
    // links [label](url) — label & url are already escaped; validate scheme
    out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, function (m, label, url) {
      var href = safeUrl(url.replace(/&amp;/g, '&'));
      if (!href) return label;
      var external = /^https?:/i.test(href);
      return '<a href="' + escapeHtml(href) + '"' +
        (external ? ' target="_blank" rel="noopener noreferrer"' : '') + '>' + label + '</a>';
    });
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    out = out.replace(/(^|[^*])\*([^*\s][^*]*?)\*/g, '$1<em>$2</em>');
    out = out.replace(/(^|[^_])_([^_\s][^_]*?)_/g, '$1<em>$2</em>');
    return out;
  }

  function isTableSeparator(line) {
    return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
  }

  function splitRow(line) {
    var trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|').map(function (c) { return c.trim(); });
  }

  function stripFrontmatter(md) {
    var s = String(md == null ? '' : md).replace(/^﻿/, '');
    s = s.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '');   // YAML frontmatter
    return s;
  }

  function renderMarkdown(md) {
    var lines = stripFrontmatter(md).replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    var html = [];
    var i = 0;

    while (i < lines.length) {
      var line = lines[i];

      if (line.trim() === '') { i++; continue; }

      // Heading
      var h = /^(#{1,6})\s+(.*)$/.exec(line);
      if (h) {
        var level = h[1].length;
        html.push('<h' + level + '>' + renderInline(h[2].trim()) + '</h' + level + '>');
        i++;
        continue;
      }

      // Table (header row followed by a separator row)
      if (line.indexOf('|') >= 0 && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
        var headers = splitRow(line);
        i += 2;
        var rows = [];
        while (i < lines.length && lines[i].indexOf('|') >= 0 && lines[i].trim() !== '') {
          rows.push(splitRow(lines[i]));
          i++;
        }
        var t = '<div class="vi-knowledge-md__table-wrap"><table><thead><tr>';
        for (var c = 0; c < headers.length; c++) t += '<th>' + renderInline(headers[c]) + '</th>';
        t += '</tr></thead><tbody>';
        for (var r = 0; r < rows.length; r++) {
          t += '<tr>';
          for (var cc = 0; cc < headers.length; cc++) t += '<td>' + renderInline(rows[r][cc] || '') + '</td>';
          t += '</tr>';
        }
        t += '</tbody></table></div>';
        html.push(t);
        continue;
      }

      // Blockquote
      if (/^\s*>\s?/.test(line)) {
        var quote = [];
        while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
          quote.push(lines[i].replace(/^\s*>\s?/, ''));
          i++;
        }
        html.push('<blockquote>' + renderInline(quote.join(' ')) + '</blockquote>');
        continue;
      }

      // Lists
      var ordered = /^\s*\d+\.\s+/.test(line);
      var unordered = /^\s*[-*+]\s+/.test(line);
      if (ordered || unordered) {
        var tag = ordered ? 'ol' : 'ul';
        var listMatch = ordered ? /^\s*\d+\.\s+/ : /^\s*[-*+]\s+/;
        var listItems = [];
        while (i < lines.length && listMatch.test(lines[i])) {
          listItems.push('<li>' + renderInline(lines[i].replace(listMatch, '').trim()) + '</li>');
          i++;
        }
        html.push('<' + tag + '>' + listItems.join('') + '</' + tag + '>');
        continue;
      }

      // Paragraph (gather until blank or a block-start line)
      var para = [];
      while (i < lines.length && lines[i].trim() !== '' &&
        !/^(#{1,6})\s+/.test(lines[i]) &&
        !/^\s*>\s?/.test(lines[i]) &&
        !/^\s*\d+\.\s+/.test(lines[i]) &&
        !/^\s*[-*+]\s+/.test(lines[i]) &&
        !(lines[i].indexOf('|') >= 0 && i + 1 < lines.length && isTableSeparator(lines[i + 1]))) {
        para.push(lines[i].trim());
        i++;
      }
      if (para.length) html.push('<p>' + renderInline(para.join(' ')) + '</p>');
    }

    return html.join('');
  }

  // ── Engine ───────────────────────────────────────────────────────────────
  function createEngine(root) {
    if (!root || root.getAttribute('data-' + NS + '-ready') === '1') return null;
    root.setAttribute('data-' + NS + '-ready', '1');

    var configEl = root.querySelector('[data-vi-knowledge-config]');
    var config;
    try {
      config = configEl ? JSON.parse(configEl.textContent) : {};
    } catch (e) {
      config = {};
    }
    config = config || {};

    var labels = deepMerge(DEFAULT_LABELS, config.labels || {});
    var files = config.files || {};
    // Flat asset base from Liquid (asset_url of a flat file with the filename
    // stripped). Runtime assets live FLAT in assets/ — Shopify does not serve
    // assets/ subfolders, so we never fetch assets/knowledge/ at runtime.
    var assetBaseUrl = typeof config.assetBaseUrl === 'string' ? config.assetBaseUrl : '';
    var assetRoot = typeof config.assetRoot === 'string' ? config.assetRoot : assetBaseUrl;
    var monolithId = config.monolithId || '';
    // Theme editor / design mode → surface developer diagnostics. Never on the
    // public storefront.
    var devMode = !!(typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode);
    function devLog() {
      if (!devMode || typeof console === 'undefined') return;
      try { console.warn.apply(console, arguments); } catch (e) { /* noop */ }
    }
    var scopeDatasets = toArray(config.datasets);
    var categories = Array.isArray(config.categories) ? config.categories : [];
    var categoryKeys = categories.map(function (c) { return normalizeCategory(c.key || c.value || c); }).filter(Boolean);
    var maxItems = parseInt(config.maxItems, 10) > 0 ? parseInt(config.maxItems, 10) : 12;
    var enableSearch = config.enableSearch !== false;
    var enableFilters = config.enableFilters !== false;
    var enableRelated = config.enableRelated !== false;
    var showSources = config.showSources !== false;
    var showDraftGlossary = config.showDraftGlossary === true;

    // DOM refs
    var el = {
      search: root.querySelector('[data-vi-search]'),
      sort: root.querySelector('[data-vi-sort]'),
      filters: root.querySelector('[data-vi-filters]'),
      chipTpl: root.querySelector('[data-vi-chip-template]'),
      meta: root.querySelector('[data-vi-meta]'),
      grid: root.querySelector('[data-vi-grid]'),
      cardTpl: root.querySelector('[data-vi-card-template]'),
      more: root.querySelector('[data-vi-more]'),
      moreBtn: root.querySelector('[data-vi-more-btn]'),
      stateLoading: root.querySelector('[data-vi-state="loading"]'),
      stateEmpty: root.querySelector('[data-vi-state="empty"]'),
      stateError: root.querySelector('[data-vi-state="error"]'),
      admin: root.querySelector('[data-vi-admin]'),
      faq: root.querySelector('[data-vi-faq]'),
      faqList: root.querySelector('[data-vi-faq-list]'),
      faqItemTpl: root.querySelector('[data-vi-faq-item-template]'),
      reader: root.querySelector('[data-vi-reader]'),
      readerOverlay: root.querySelector('[data-vi-reader-overlay]'),
      readerDialog: root.querySelector('[data-vi-reader-dialog]'),
      readerEyebrow: root.querySelector('[data-vi-reader-eyebrow]'),
      readerTitle: root.querySelector('[data-vi-reader-title]'),
      readerBody: root.querySelector('[data-vi-reader-body]'),
      readerClose: root.querySelector('[data-vi-reader-close]'),
      relatedTpl: root.querySelector('[data-vi-related-template]'),
      relatedLinkTpl: root.querySelector('[data-vi-related-link-template]')
    };

    var state = {
      items: [],
      bySlug: {},
      related: {},
      contentMap: {},
      manifest: null,
      slugBundle: {},      // slug -> bundle id (from manifest)
      bundleCache: {},     // bundle id -> { slug: fullItem }
      monolithCache: {},   // monolith id -> { slug: fullItem }
      faqByDataset: {},
      query: '',
      category: normalizeCategory(config.defaultCategory) || 'all',
      sort: 'relevance',
      filtered: [],
      renderLimit: maxItems,
      readerSlug: null,
      lastFocus: null,
      keyHandler: null,
      failedAsset: null
    };

    function emit(name, detail) {
      try {
        root.dispatchEvent(new CustomEvent(NS + ':' + name, { bubbles: true, detail: detail || {} }));
      } catch (e) { /* CustomEvent unsupported — non-fatal */ }
    }

    function showState(which) {
      if (el.stateLoading) el.stateLoading.hidden = which !== 'loading';
      if (el.stateEmpty) el.stateEmpty.hidden = which !== 'empty';
      if (el.stateError) el.stateError.hidden = which !== 'error';
      var showGrid = which === 'results';
      if (el.grid) el.grid.hidden = !showGrid;
      if (el.meta) el.meta.hidden = !showGrid;
      if (el.more && !showGrid) el.more.hidden = true;
    }

    // Shopify's assets directory is conventionally flat. We reference the
    // canonical subdirectory path from Liquid (assets/knowledge/X.json) and
    // derive a flat fallback (assets/knowledge-X.json) here at runtime, so the
    // feature keeps working if an operator flattens the files — without adding
    // a static asset_url that theme-check would flag as MissingAsset.
    function expandCandidates(list) {
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var u = String(list[i]);
        if (out.indexOf(u) < 0) out.push(u);
        var flat = u.replace(/(^|\/)knowledge\//, '$1knowledge-');
        if (flat !== u && out.indexOf(flat) < 0) out.push(flat);
      }
      return out;
    }

    // Robust fetch over an ordered list of candidate URLs (no derivation).
    function fetchJsonRaw(list) {
      var idx = 0;
      function attempt() {
        if (idx >= list.length) return Promise.reject(new Error('no candidate resolved'));
        var url = list[idx++];
        if (!url) return attempt();
        return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (res) {
            if (!res.ok) throw new Error('status ' + res.status);
            return res.json();
          })
          .catch(function () { return attempt(); });
      }
      return attempt();
    }

    // Adds a derived flat fallback (knowledge/X -> knowledge-X) for the initial
    // indexes / manifest / monoliths, then fetches in order.
    function fetchJson(candidates) {
      return fetchJsonRaw(expandCandidates(toArray(candidates)));
    }

    function basename(p) { return String(p || '').split('?')[0].split('/').pop(); }

    // Runtime URL for a FLAT asset filename (manifest `file`, e.g.
    // "knowledge-bundle-druvor-a-f.json") resolved against the Liquid asset base.
    // This is the only runtime asset path — never an assets/ subfolder.
    function flatUrl(file) {
      var f = String(file || '');
      if (!f) return '';
      if (/^https?:|^\/\//.test(f)) return f;            // already absolute (from Liquid)
      return assetBaseUrl ? assetBaseUrl + f : f;
    }

    // First resolvable filename from a candidate list (for the admin diagnostic).
    function firstFilename(cands) {
      var arr = toArray(cands);
      for (var i = 0; i < arr.length; i++) { var b = basename(arr[i]); if (b) return b; }
      return '';
    }

    function extractItems(data) {
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.items)) return data.items;
      if (data && Array.isArray(data.content)) return data.content;
      if (data && Array.isArray(data.entries)) return data.entries;
      return [];
    }

    function normalizeItem(raw) {
      var slug = slugify(firstString(raw.slug, raw.handle, raw.id, raw.title));
      var baseCat = normalizeCategory(firstString(raw.category, raw.type, raw.group, raw.kind));
      var color = slugify(firstString(raw.color, raw.colour));
      var cfold = asciiFold(color).toLowerCase(); // accent-insensitive (röd -> rod)
      // Refine generic grapes into red/white using the colour field so the
      // existing red/white chips and card colour-coding work with real data.
      var category = baseCat;
      if (baseCat === 'grape' && cfold) {
        if (/(^|-)(rod|rott|red)($|-)/.test(cfold)) category = 'red-grape';
        else if (/(^|-)(vit|white|gron|green)($|-)/.test(cfold)) category = 'white-grape';
      }
      var dataset = firstString(raw.dataset, raw.bundle, raw.source_file);
      return {
        slug: slug,
        id: firstString(raw.id, slug),
        title: firstString(raw.title, raw.name, raw.heading, slug),
        type: baseCat,
        category: category,
        color: color,
        dataset: dataset,
        excerpt: firstString(raw.excerpt, raw.summary, raw.description, raw.intro, raw.teaser, raw.seo_description),
        tags: toArray(raw.tags),
        keywords: toArray(raw.keywords || raw.synonyms),
        status: slugify(firstString(raw.status)) || 'published',
        verdict: normalizeVerdict(firstString(raw.verdict, raw.truth)),
        subcategory: normalizeSubcat(firstString(raw.subcategory)),
        subcats: toArray(raw.subcategories).map(normalizeSubcat).filter(Boolean),
        related_count: typeof raw.related_count === 'number' ? raw.related_count : null,
        sources: toArray(raw.sources),
        content_markdown: firstString(raw.content_markdown, raw.content, raw.body, raw.markdown)
      };
    }

    function inScope(item) {
      if (scopeDatasets.length) {
        if (item.dataset && scopeDatasets.indexOf(item.dataset) >= 0) return true;
        if (item.dataset && scopeDatasets.indexOf(item.dataset) < 0) {
          // explicit dataset that is not ours — still allow if category matches our chips
          if (categoryKeys.length) return categoryKeys.some(function (k) { return matchesChip(item, k); });
          return false;
        }
      }
      if (categoryKeys.length) return categoryKeys.some(function (k) { return matchesChip(item, k); });
      return true;
    }

    // Match an item against a chip. Wine-style subcategory chips match on the
    // item's subcats (vinstilar only); everything else matches on category.
    function matchesChip(item, chipKey) {
      if (!chipKey || chipKey === 'all') return true;
      if (STYLE_SUBCAT[chipKey]) {
        return item.category === 'style' && item.subcats && item.subcats.indexOf(STYLE_SUBCAT[chipKey]) >= 0;
      }
      if (item.category === chipKey) return true;
      var group = CATEGORY_GROUPS[chipKey];
      return !!(group && group.indexOf(item.category) >= 0);
    }

    function typeLabel(item) {
      return labels.types[item.category] || labels.types[item.type] ||
        (item.category ? item.category.replace(/-/g, ' ') : '');
    }

    function relatedCount(item) {
      if (typeof item.related_count === 'number') return item.related_count;
      return state.related[item.slug] ? state.related[item.slug].length : 0;
    }

    function haystack(item) {
      return [item.title, item.slug, item.excerpt, item.tags.join(' '),
        item.keywords.join(' '), item.category, item.type, item.color,
        item.verdict ? (labels.verdict[item.verdict] || '') : ''].join(' ').toLowerCase();
    }

    function applyFilters() {
      var tokens = state.query.toLowerCase().split(/\s+/).filter(Boolean);
      var list = state.items.filter(function (item) {
        if (!matchesChip(item, state.category)) return false;
        if (tokens.length) {
          var hay = haystack(item);
          for (var t = 0; t < tokens.length; t++) if (hay.indexOf(tokens[t]) < 0) return false;
        }
        return true;
      });

      if (state.sort === 'title-asc') list.sort(function (a, b) { return a.title.localeCompare(b.title, 'sv'); });
      else if (state.sort === 'title-desc') list.sort(function (a, b) { return b.title.localeCompare(a.title, 'sv'); });
      else if (state.sort === 'related-desc') list.sort(function (a, b) { return relatedCount(b) - relatedCount(a); });
      else if (state.category !== 'glossary') {
        // relevance/default: keep source order but push the (draft) glossary
        // last so it never dominates the mixed list when it is enabled.
        list.sort(function (a, b) { return (a.category === 'glossary' ? 1 : 0) - (b.category === 'glossary' ? 1 : 0); });
      }

      state.filtered = list;
      state.renderLimit = maxItems;
      render();
    }

    function render() {
      if (!el.grid) return;
      var list = state.filtered;

      if (!list.length) {
        showState('empty');
        emit('rendered', { items: [] });
        return;
      }

      showState('results');
      var slice = list.slice(0, state.renderLimit);
      var frag = document.createDocumentFragment();
      for (var i = 0; i < slice.length; i++) {
        var card = buildCard(slice[i]);
        if (card) frag.appendChild(card);
      }
      el.grid.textContent = '';
      el.grid.appendChild(frag);

      if (el.meta) {
        el.meta.textContent = list.length === 1 ? labels.resultsOne : format(labels.results, list.length);
      }
      if (el.more) {
        var hasMore = list.length > state.renderLimit;
        el.more.hidden = !hasMore;
        if (el.moreBtn) el.moreBtn.textContent = labels.loadMore;
      }
      emit('rendered', { items: slice });
    }

    function buildCard(item) {
      if (!el.cardTpl || !el.cardTpl.content) return null;
      var node = el.cardTpl.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-slug', item.slug);
      node.setAttribute('data-category', item.category);
      node.setAttribute('data-type', item.type);

      setText(node, '[data-card-type]', typeLabel(item));

      // Verdikt-taggen ("MYT"/"SANT") visas inte längre på korten — domen
      // presenteras i läsaren där själva svaret hör hemma.
      var verdictEl = node.querySelector('[data-card-verdict]');
      if (verdictEl) verdictEl.remove();

      setText(node, '[data-card-title]', item.title);

      var exEl = node.querySelector('[data-card-excerpt]');
      if (exEl) {
        if (item.excerpt) exEl.textContent = item.excerpt;
        else exEl.remove();
      }

      var tagsEl = node.querySelector('[data-card-tags]');
      if (tagsEl) {
        tagsEl.textContent = '';
        var tags = item.tags.slice(0, 4);
        if (tags.length) {
          for (var t = 0; t < tags.length; t++) {
            var li = document.createElement('li');
            li.className = 'vi-knowledge-card__tag';
            li.textContent = tags[t];
            tagsEl.appendChild(li);
          }
        } else {
          tagsEl.remove();
        }
      }

      var relEl = node.querySelector('[data-card-related]');
      if (relEl) {
        var n = relatedCount(item);
        if (enableRelated && n > 0) relEl.textContent = format(labels.relatedCount, n);
        else relEl.remove();
      }

      var btn = node.querySelector('[data-card-button]');
      if (btn) {
        btn.textContent = labels.readMore;
        btn.setAttribute('data-slug', item.slug);
        btn.setAttribute('aria-label', labels.readMore + ': ' + item.title);
      }
      return node;
    }

    function setText(scope, selector, value) {
      var node = scope.querySelector(selector);
      if (node) node.textContent = value;
    }

    // ── Filter chips ────────────────────────────────────────────────────────
    function buildChips() {
      if (!enableFilters || !el.filters || !el.chipTpl || !el.chipTpl.content) {
        if (el.filters) el.filters.hidden = true;
        return;
      }
      var defs = [{ key: 'all', label: labels.all }].concat(categories.map(function (c) {
        return { key: normalizeCategory(c.key || c.value || c), label: c.label || c.name || (c.key || c.value) };
      }));

      el.filters.textContent = '';
      var rendered = 0;
      for (var i = 0; i < defs.length; i++) {
        var def = defs[i];
        var count = def.key === 'all'
          ? state.items.length
          : state.items.filter(function (it) { return matchesChip(it, def.key); }).length;
        if (def.key !== 'all' && count === 0) continue; // "om data finns"

        var chipNode = el.chipTpl.content.firstElementChild.cloneNode(true);
        chipNode.setAttribute('data-category', def.key);
        chipNode.setAttribute('aria-pressed', def.key === state.category ? 'true' : 'false');
        setText(chipNode, '[data-chip-label]', def.label);
        var countEl = chipNode.querySelector('[data-chip-count]');
        if (countEl) countEl.textContent = count;
        el.filters.appendChild(chipNode);
        rendered++;
      }
      el.filters.hidden = rendered <= 1;
    }

    function setCategory(key) {
      state.category = key || 'all';
      var chips = el.filters ? el.filters.querySelectorAll('[data-category]') : [];
      for (var i = 0; i < chips.length; i++) {
        chips[i].setAttribute('aria-pressed', chips[i].getAttribute('data-category') === state.category ? 'true' : 'false');
      }
      applyFilters();
    }

    // ── FAQ ──────────────────────────────────────────────────────────────────
    function renderFaq() {
      if (!el.faq || !el.faqList || !el.faqItemTpl || !el.faqItemTpl.content) return;
      var faq = [];
      if (config.page && state.faqByDataset[config.page]) {
        faq = state.faqByDataset[config.page];
      } else if (scopeDatasets.length === 1 && state.faqByDataset[scopeDatasets[0]]) {
        faq = state.faqByDataset[scopeDatasets[0]];
      } else if (Array.isArray(state.faqByDataset.all)) {
        faq = state.faqByDataset.all;
      }
      faq = Array.isArray(faq) ? faq : [];
      if (!faq.length) { el.faq.hidden = true; return; }

      el.faqList.textContent = '';
      for (var i = 0; i < faq.length; i++) {
        var q = firstString(faq[i].question, faq[i].q, faq[i].title);
        var a = firstString(faq[i].answer, faq[i].a, faq[i].text);
        if (!q) continue;
        var node = el.faqItemTpl.content.firstElementChild.cloneNode(true);
        setText(node, '[data-faq-q]', q);
        var aEl = node.querySelector('[data-faq-a]');
        if (aEl) aEl.innerHTML = renderMarkdown(a); // a is rendered via the safe renderer
        el.faqList.appendChild(node);
      }
      el.faq.hidden = false;
    }

    // ── Reader modal: manifest-driven lazy full-text loading ───────────────────
    function indexFull(data) {
      var map = {};
      var arr = extractItems(data);
      for (var i = 0; i < arr.length; i++) {
        var it = normalizeItem(arr[i]);
        if (it.slug) map[it.slug] = it;
      }
      return map;
    }

    function loadBundle(bundleId) {
      if (state.bundleCache[bundleId] !== undefined) return Promise.resolve(state.bundleCache[bundleId]);
      var def = null;
      var bundles = (state.manifest && state.manifest.bundles) || [];
      for (var i = 0; i < bundles.length; i++) { if (bundles[i].id === bundleId) { def = bundles[i]; break; } }
      if (!def) return Promise.resolve(null);
      var file = def.file || basename(def.fallback_path) || basename(def.path);
      var candidates = [flatUrl(file)].filter(Boolean);
      return fetchJsonRaw(candidates).then(function (data) {
        state.bundleCache[bundleId] = indexFull(data);
        return state.bundleCache[bundleId];
      }).catch(function () { devLog('[vi-knowledge] bundle failed:', file); state.bundleCache[bundleId] = null; return null; });
    }

    function loadMonolith(id) {
      if (!id) return Promise.resolve(null);
      if (state.monolithCache[id] !== undefined) return Promise.resolve(state.monolithCache[id]);
      var candidates = [];
      var mono = state.manifest && state.manifest.monoliths && state.manifest.monoliths[id];
      if (mono) candidates.push(flatUrl(mono.file || basename(mono.fallback_path)));
      var cfg = (files.monolith && files.monolith[id]) ? files.monolith[id] : [];
      candidates = candidates.concat(toArray(cfg)).filter(Boolean);
      return fetchJsonRaw(candidates).then(function (data) {
        state.monolithCache[id] = indexFull(data);
        return state.monolithCache[id];
      }).catch(function () { state.monolithCache[id] = null; return null; });
    }

    // Resolve the full item for a slug: manifest bundle -> monolith fallback.
    function loadFullItem(slug) {
      var bundleId = state.slugBundle[slug];
      if (bundleId) {
        return loadBundle(bundleId).then(function (map) {
          if (map && map[slug]) return map[slug];
          return loadMonolith(monolithId).then(function (m) { return (m && m[slug]) || null; });
        });
      }
      return loadMonolith(monolithId).then(function (m) { return (m && m[slug]) || null; });
    }

    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function openReader(slug) {
      var item = state.bySlug[slug];
      if (!item || !el.reader) return;
      state.readerSlug = slug;
      if (!el.reader.hasAttribute('hidden')) {
        // already open (related navigation) — just refresh content
        fillReader(item);
        return;
      }
      state.lastFocus = document.activeElement;
      el.reader.hidden = false;
      document.body.classList.add('vi-knowledge-reader-open');

      state.keyHandler = function (ev) {
        if (ev.key === 'Escape') { ev.preventDefault(); closeReader(); return; }
        if (ev.key === 'Tab') trapFocus(ev);
      };
      document.addEventListener('keydown', state.keyHandler);

      fillReader(item);
      if (el.readerClose) el.readerClose.focus();
      emit('open', { slug: slug, item: item });
    }

    function fillReader(item) {
      if (el.readerEyebrow) el.readerEyebrow.textContent = typeLabel(item);
      if (el.readerTitle) el.readerTitle.textContent = item.title;
      if (!el.readerBody) return;
      el.readerBody.innerHTML = '<p class="vi-knowledge-reader__loading">' + escapeHtml(labels.readerLoading) + '</p>';
      state.readerSlug = item.slug;

      loadFullItem(item.slug).then(function (loaded) {
        if (state.readerSlug !== item.slug) return; // user navigated away
        var full = loaded || item;
        var md = full.content_markdown || item.content_markdown || '';
        var parts = [];
        // Verdict badge (myter). Only rendered for a known verdict; an unknown
        // or missing verdict is simply omitted (never shown as error text).
        var verdict = item.verdict || full.verdict || null;
        if (verdict && labels.verdict[verdict]) {
          parts.push('<p class="vi-knowledge-reader__verdict" data-verdict="' + escapeHtml(verdict) + '">' +
            escapeHtml(labels.verdict[verdict]) + '</p>');
        }
        if (md) {
          parts.push('<div class="vi-knowledge-md">' + renderMarkdown(md) + '</div>');
        } else {
          parts.push('<div class="vi-knowledge-md"><p>' + escapeHtml(item.excerpt || labels.readerFallback) + '</p>' +
            (item.excerpt ? '<p><em>' + escapeHtml(labels.readerFallback) + '</em></p>' : '') + '</div>');
        }
        el.readerBody.innerHTML = parts.join('');

        if (showSources && full.sources && full.sources.length) {
          appendSources(full.sources);
        }
        if (enableRelated) appendRelated(item.slug);
      });
    }

    function appendSources(sources) {
      var wrap = document.createElement('div');
      wrap.className = 'vi-knowledge-reader__sources';
      var title = document.createElement('strong');
      title.textContent = labels.sources;
      wrap.appendChild(title);
      var ul = document.createElement('ul');
      for (var i = 0; i < sources.length; i++) {
        var li = document.createElement('li');
        li.textContent = sources[i];
        ul.appendChild(li);
      }
      wrap.appendChild(ul);
      el.readerBody.appendChild(wrap);
    }

    function appendRelated(slug) {
      var rel = state.related[slug];
      if (!rel || !rel.length || !el.relatedTpl || !el.relatedTpl.content) return;
      var available = rel.filter(function (s) { return state.bySlug[slugify(s)]; });
      if (!available.length) return;

      var block = el.relatedTpl.content.firstElementChild.cloneNode(true);
      setText(block, '[data-vi-related-title]', labels.related);
      var listEl = block.querySelector('[data-vi-related-list]');
      if (listEl && el.relatedLinkTpl && el.relatedLinkTpl.content) {
        for (var i = 0; i < available.length; i++) {
          var target = state.bySlug[slugify(available[i])];
          var linkNode = el.relatedLinkTpl.content.firstElementChild.cloneNode(true);
          var btn = linkNode.querySelector('[data-related-slug]') || linkNode;
          btn.setAttribute('data-related-slug', target.slug);
          setText(linkNode, '[data-related-label]', target.title);
          listEl.appendChild(linkNode);
        }
      }
      el.readerBody.appendChild(block);
    }

    function closeReader() {
      if (!el.reader) return;
      el.reader.hidden = true;
      document.body.classList.remove('vi-knowledge-reader-open');
      if (state.keyHandler) { document.removeEventListener('keydown', state.keyHandler); state.keyHandler = null; }
      state.readerSlug = null;
      if (state.lastFocus && typeof state.lastFocus.focus === 'function') state.lastFocus.focus();
    }

    function trapFocus(ev) {
      if (!el.readerDialog) return;
      var nodes = Array.prototype.slice.call(el.readerDialog.querySelectorAll(FOCUSABLE))
        .filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
      if (!nodes.length) { el.readerDialog.focus(); ev.preventDefault(); return; }
      var first = nodes[0], last = nodes[nodes.length - 1];
      if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
    }

    // ── Events ────────────────────────────────────────────────────────────────
    function bindEvents() {
      if (enableSearch && el.search) {
        el.search.addEventListener('input', debounce(function () {
          state.query = el.search.value || '';
          applyFilters();
        }, 180));
      }
      if (el.sort) {
        el.sort.addEventListener('change', function () { state.sort = el.sort.value || 'relevance'; applyFilters(); });
      }
      if (el.filters) {
        el.filters.addEventListener('click', function (ev) {
          var chip = ev.target.closest('[data-category]');
          if (chip) setCategory(chip.getAttribute('data-category'));
        });
      }
      // Premium presentational areas reuse the engine: a featured-guide card
      // (data-vi-open="<slug>") opens the reader; a category-overview card
      // (data-vi-cat="<key>") focuses the browser on that category.
      root.addEventListener('click', function (ev) {
        var open = ev.target.closest('[data-vi-open]');
        if (open && root.contains(open)) { ev.preventDefault(); openReader(open.getAttribute('data-vi-open')); return; }
        var cat = ev.target.closest('[data-vi-cat]');
        if (cat && root.contains(cat)) {
          ev.preventDefault();
          setCategory(cat.getAttribute('data-vi-cat'));
          var browser = root.querySelector('[data-vi-browser]') || el.grid;
          if (browser && typeof browser.scrollIntoView === 'function') {
            browser.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
      if (el.moreBtn) {
        el.moreBtn.addEventListener('click', function () { state.renderLimit += maxItems; render(); });
      }
      if (el.grid) {
        el.grid.addEventListener('click', function (ev) {
          var btn = ev.target.closest('[data-card-button]');
          if (btn) { ev.preventDefault(); openReader(btn.getAttribute('data-slug')); }
        });
      }
      if (el.readerClose) el.readerClose.addEventListener('click', closeReader);
      if (el.readerOverlay) el.readerOverlay.addEventListener('click', closeReader);
      if (el.readerBody) {
        el.readerBody.addEventListener('click', function (ev) {
          var rl = ev.target.closest('[data-related-slug]');
          if (rl) { ev.preventDefault(); openReader(rl.getAttribute('data-related-slug')); }
        });
      }
    }

    // Build slug -> bundle id routing from the manifest.
    function applyManifest(m) {
      state.manifest = m || null;
      var bundles = (m && m.bundles) || [];
      for (var i = 0; i < bundles.length; i++) {
        var slugs = bundles[i].slugs || [];
        for (var j = 0; j < slugs.length; j++) state.slugBundle[slugify(slugs[j])] = bundles[i].id;
      }
    }

    // Accept both { relations: {slug:[...]} } and { items: [{slug, related}] }.
    function buildRelations(d) {
      if (d && d.relations && typeof d.relations === 'object') return d.relations;
      var map = {};
      var arr = extractItems(d);
      for (var i = 0; i < arr.length; i++) {
        var r = arr[i];
        if (r && r.slug && Array.isArray(r.related)) map[slugify(r.slug)] = r.related.map(slugify);
      }
      return map;
    }

    // Ingest a light index dataset into state.items (scoped + de-duped). Called
    // for the public index always, and for the glossary index on demand.
    function ingest(data) {
      var raw = extractItems(data);
      for (var i = 0; i < raw.length; i++) {
        var it = normalizeItem(raw[i]);
        if (!it.slug || state.bySlug[it.slug]) continue;
        if (!inScope(it)) continue;
        state.bySlug[it.slug] = it;
        state.items.push(it);
      }
    }

    // ── Topic-area controllers: per-section local search + subcategory chips ──
    // Each [data-vi-area] block gets its own search + optional subcategory chips
    // scoped to that area's category. The curated featured cards are the default
    // (and no-JS) view; filtered results replace them only when the visitor
    // searches or selects a subcategory. Reader opening stays on the shared
    // in-root data-vi-open delegation — the browser at the bottom is untouched.
    function buildAreaCard(item, overrides) {
      var li = document.createElement('li');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'vi-knowledge__featured-card';
      btn.setAttribute('data-vi-open', item.slug);
      // Optional manual override (Theme Editor: "Kortbilder") keyed by slug.
      var ov = (overrides && item.slug && overrides[item.slug]) ? overrides[item.slug] : null;
      var media = document.createElement('span');
      media.setAttribute('aria-hidden', 'true');
      if (ov && ov.image) {
        // Manual image — matches the server-rendered featured cards exactly.
        media.className = 'vi-knowledge__featured-media';
        var img = document.createElement('img');
        img.className = 'vi-knowledge__featured-image';
        img.src = ov.image;
        img.alt = '';
        img.width = 1200;
        img.height = 675;
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);
      } else {
        // Premium fallback mark (first letter) so JS-built cards match the
        // server-rendered featured cards visually — no empty grey media area.
        media.className = 'vi-knowledge__featured-media vi-knowledge__featured-media--fallback';
        var mark = document.createElement('span');
        mark.className = 'vi-knowledge__featured-mark';
        mark.textContent = String(item.title || '').charAt(0).toUpperCase();
        media.appendChild(mark);
      }
      btn.appendChild(media);
      var eyebrow = document.createElement('p');
      eyebrow.className = 'vi-knowledge__featured-eyebrow';
      eyebrow.textContent = typeLabel(item);
      btn.appendChild(eyebrow);
      // Verdikt-taggen ("MYT") visas inte på kort — endast i läsaren.
      var title = document.createElement('h3');
      title.className = 'vi-knowledge__featured-title';
      title.textContent = item.title;
      btn.appendChild(title);
      // Optional manual short text (override) — premium one-liner under the title.
      if (ov && ov.text) {
        var text = document.createElement('p');
        text.className = 'vi-knowledge__featured-text';
        text.textContent = ov.text;
        btn.appendChild(text);
      }
      var link = document.createElement('span');
      link.className = 'vi-knowledge__featured-link';
      link.setAttribute('aria-hidden', 'true');
      link.textContent = labels.readMore;
      btn.appendChild(link);
      li.appendChild(btn);
      return li;
    }

    function setupArea(areaEl) {
      if (areaEl.getAttribute('data-vi-area-ready') === '1') return;
      areaEl.setAttribute('data-vi-area-ready', '1');
      var catKey = normalizeCategory(areaEl.getAttribute('data-vi-area'));
      var base = state.items.filter(function (it) { return matchesChip(it, catKey); });
      var searchEl = areaEl.querySelector('[data-vi-area-search]');
      var countEl = areaEl.querySelector('[data-vi-area-count]');
      var clearEl = areaEl.querySelector('[data-vi-area-clear]');
      var filtersEl = areaEl.querySelector('[data-vi-area-filters]');
      var featuredEl = areaEl.querySelector('[data-vi-area-featured]');
      var resultsEl = areaEl.querySelector('[data-vi-area-results]');
      var emptyEl = areaEl.querySelector('[data-vi-area-empty]');
      var moreWrap = areaEl.querySelector('[data-vi-area-more]');
      var moreBtn = areaEl.querySelector('[data-vi-area-more-btn]');
      // Manual card-image overrides (Nivå 3): slug -> {image, text}, emitted by
      // the area snippet as <script data-vi-area-overrides>. Empty/invalid = none.
      var overridesEl = areaEl.querySelector('[data-vi-area-overrides]');
      var areaOverrides = {};
      if (overridesEl) {
        try { areaOverrides = JSON.parse(overridesEl.textContent || '{}') || {}; }
        catch (e) { areaOverrides = {}; }
      }
      var chips = filtersEl ? Array.prototype.slice.call(filtersEl.querySelectorAll('[data-vi-area-filter]')) : [];
      var as = { query: '', sub: 'all', limit: maxItems };

      // Subcategory chip counts from real data; hide empty ones ("om data finns").
      for (var i = 0; i < chips.length; i++) {
        var key = chips[i].getAttribute('data-vi-area-filter');
        var n = key === 'all' ? base.length : base.filter(function (it) { return matchesChip(it, key); }).length;
        var cEl = chips[i].querySelector('[data-area-count]');
        if (cEl) cEl.textContent = n;
        if (key !== 'all' && n === 0) chips[i].hidden = true;
      }

      function isIdle() { return !as.query && as.sub === 'all'; }
      function visible() {
        var toks = as.query.toLowerCase().split(/\s+/).filter(Boolean);
        return base.filter(function (it) {
          if (as.sub !== 'all' && !matchesChip(it, as.sub)) return false;
          if (toks.length) { var h = haystack(it); for (var t = 0; t < toks.length; t++) if (h.indexOf(toks[t]) < 0) return false; }
          return true;
        });
      }
      function setCount(n, idle) {
        if (!countEl) return;
        countEl.textContent = idle ? (n + ' ' + (n === 1 ? 'artikel' : 'artiklar')) : (n === 1 ? labels.resultsOne : format(labels.results, n));
      }
      function renderArea() {
        for (var i = 0; i < chips.length; i++) {
          chips[i].setAttribute('aria-pressed', chips[i].getAttribute('data-vi-area-filter') === as.sub ? 'true' : 'false');
        }
        if (clearEl) clearEl.hidden = isIdle();
        if (isIdle()) {
          if (featuredEl) featuredEl.hidden = false;
          if (resultsEl) { resultsEl.hidden = true; resultsEl.textContent = ''; }
          if (emptyEl) emptyEl.hidden = true;
          if (moreWrap) moreWrap.hidden = true;
          setCount(base.length, true);
          return;
        }
        if (featuredEl) featuredEl.hidden = true;
        var list = visible();
        setCount(list.length, false);
        if (!list.length) {
          if (resultsEl) { resultsEl.hidden = true; resultsEl.textContent = ''; }
          if (moreWrap) moreWrap.hidden = true;
          if (emptyEl) emptyEl.hidden = false;
          return;
        }
        if (emptyEl) emptyEl.hidden = true;
        if (resultsEl) {
          resultsEl.hidden = false;
          var frag = document.createDocumentFragment();
          var slice = list.slice(0, as.limit);
          for (var j = 0; j < slice.length; j++) frag.appendChild(buildAreaCard(slice[j], areaOverrides));
          resultsEl.textContent = '';
          resultsEl.appendChild(frag);
        }
        if (moreWrap) moreWrap.hidden = list.length <= as.limit;
      }

      if (searchEl) {
        searchEl.addEventListener('input', debounce(function () { as.query = searchEl.value || ''; as.limit = maxItems; renderArea(); }, 160));
      }
      if (clearEl) {
        clearEl.addEventListener('click', function () { as.query = ''; as.sub = 'all'; as.limit = maxItems; if (searchEl) searchEl.value = ''; renderArea(); });
      }
      if (filtersEl) {
        filtersEl.addEventListener('click', function (ev) {
          var chip = ev.target.closest('[data-vi-area-filter]');
          if (!chip) return;
          as.sub = chip.getAttribute('data-vi-area-filter');
          as.limit = maxItems;
          renderArea();
        });
      }
      if (moreBtn) {
        moreBtn.addEventListener('click', function () { as.limit += maxItems; renderArea(); });
      }

      setCount(base.length, true);
    }

    function initAreas() {
      var areas = root.querySelectorAll('[data-vi-area]');
      for (var i = 0; i < areas.length; i++) setupArea(areas[i]);
    }

    // ── Boot ────────────────────────────────────────────────────────────────
    function start() {
      showState('loading');
      bindEvents();

      // Manifest first (optional — routing falls back to the monolith if absent).
      fetchJson(toArray(files.manifest))
        .then(function (m) { applyManifest(m); })
        .catch(function () { state.manifest = null; devLog('[vi-knowledge] manifest not loaded:', firstFilename(files.manifest)); })
        .then(function () {
          // The public index is required; record its filename if it cannot load.
          return fetchJson(toArray(files.searchIndex)).catch(function (e) {
            state.failedAsset = firstFilename(files.searchIndex);
            devLog('[vi-knowledge] public index failed:', state.failedAsset);
            throw e;
          });
        })
        .then(function (data) {
          state.faqByDataset = (data && data.faq_by_dataset) ? data.faq_by_dataset : {};
          ingest(data);

          // Draft glossary (750 terms) is loaded ONLY in preview/dev
          // (show_draft_glossary) and only on a page that has a glossary chip,
          // so the public first paint never carries it.
          var wantGlossary = showDraftGlossary && categoryKeys.indexOf('glossary') >= 0;
          var glossaryStep = wantGlossary
            ? fetchJson(toArray(files.glossarySearchIndex)).then(ingest).catch(function () {})
            : Promise.resolve();

          return glossaryStep.then(function () {
            // Optional light maps (non-fatal if missing).
            return Promise.all([
              fetchJson(toArray(files.relatedMap)).then(function (d) { state.related = buildRelations(d); }).catch(function () { state.related = {}; }),
              fetchJson(toArray(files.contentMap)).then(function (d) { state.contentMap = d || {}; }).catch(function () { state.contentMap = {}; })
            ]);
          });
        })
        .then(function () {
          if (!state.items.length) { showEmptyOrError('empty'); return; }
          buildChips();
          if (state.category !== 'all' && !state.items.some(function (it) { return matchesChip(it, state.category); })) {
            state.category = 'all';
          }
          renderFaq();
          applyFilters();
          initAreas();
          emit('ready', { count: state.items.length });
        })
        .catch(function () { showEmptyOrError('error'); });
    }

    function showEmptyOrError(kind) {
      showState(kind === 'empty' ? 'empty' : 'error');
      if (kind === 'empty' || !el.admin) return;
      // Public sees only the discrete message in the error state. The admin
      // notice (with the exact missing filename) is shown ONLY in the theme
      // editor / design mode, never on the live storefront.
      if (devMode) {
        var miss = state.failedAsset ? ' Saknad asset: ' + state.failedAsset : '';
        el.admin.textContent = labels.adminText + miss;
        el.admin.hidden = false;
      } else {
        el.admin.hidden = true;
      }
    }

    start();
    return { root: root, refresh: applyFilters, open: openReader, close: closeReader };
  }

  // ── Auto-init ───────────────────────────────────────────────────────────
  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-vi-knowledge]');
    for (var i = 0; i < roots.length; i++) createEngine(roots[i]);
  }

  window.ViKnowledge = window.ViKnowledge || {};
  window.ViKnowledge.init = initAll;
  window.ViKnowledge.createEngine = createEngine;
  window.ViKnowledge.renderMarkdown = renderMarkdown;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-init when a section is added/edited in the Shopify theme editor.
  document.addEventListener('shopify:section:load', function (ev) {
    if (ev && ev.target) initAll(ev.target);
  });

  /* ══════════════════════════════════════════════════════════════════════
     MOBILE COMPACT LAYER (additiv modul — ändrar inga befintliga funktioner)
     ------------------------------------------------------------------------
     Gör ämnesområdena (inkl. Vinatlas) till dragspel på små skärmar:
       • Injicerar en riktig <button class="vi-knowledge__area-toggle"> med
         aria-expanded/aria-controls i varje områdes section-head, plus ett
         antal-chip som speglar motorns/kartans befintliga counts
         ([data-vi-area-count] / [data-vi-region-count]).
       • Togglar ENBART klassen is-mobile-collapsed på området — innehållet
         döljs av CSS-regler som ligger inne i @media (max-width: 749px)
         (vi-knowledge-hub.css, "MOBILKOMPAKT LAGER"), så klassen är helt
         verkningslös på desktop.
       • Allt är gated bakom window.matchMedia('(max-width: 749px)') med
         change-lyssnare: går man över 749px expanderas allt, klasserna tas
         bort och knappen/chipet får [hidden] — utan omladdning. Går man
         under gränsen fälls allt ihop igen (startläget på mobil).
       • Extra click-lyssnare på roten (motorns egen delegation är orörd):
         ett kategorikort ([data-vi-cat]) auto-expanderar matchande område
         innan motorns scroll tar vid. Den globala utforska-panelen är
         aldrig hopfälld, så global sök behöver ingen extra hantering.
     Inga DOM-noder tas bort och inga data-attribut ändras — dolt innehåll
     är fortfarande initierat av motorn och fungerar direkt vid expandering.
     Saknas matchMedia (t.ex. QA-shimmen i scripts/qa-knowledge-preview.mjs)
     är modulen en no-op.
     ══════════════════════════════════════════════════════════════════════ */
  (function () {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (typeof window.matchMedia !== 'function' || !document.querySelectorAll) return;
    var mql;
    try { mql = window.matchMedia('(max-width: 749px)'); } catch (e) { mql = null; }
    if (!mql) return;

    var COLLAPSED = 'is-mobile-collapsed';
    var enhanced = [];   // [{ root, areas: [{ area, btn, chip, countEl }] }]
    var uidSeq = 0;

    function isMobile() { return !!mql.matches; }

    // Befintlig count-nod: standardområde -> [data-vi-area-count],
    // Vinatlas -> [data-vi-region-count]. Kan saknas (t.ex. local_search av).
    function areaCountSource(area) {
      return area.querySelector('[data-vi-area-count]') || area.querySelector('[data-vi-region-count]');
    }

    function syncChip(entry) {
      if (!entry.chip) return;
      if (!entry.countEl) entry.countEl = areaCountSource(entry.area);
      var txt = entry.countEl && entry.countEl.textContent ? String(entry.countEl.textContent).trim() : '';
      if (entry.chip.textContent !== txt) entry.chip.textContent = txt;
    }

    function setExpanded(entry, expanded) {
      if (expanded) entry.area.classList.remove(COLLAPSED);
      else entry.area.classList.add(COLLAPSED);
      if (entry.btn) entry.btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    function buildToggle(area) {
      var head = area.querySelector('.vi-knowledge__section-head');
      var main = area.querySelector('.vi-knowledge__area-main');
      if (!head || !main) return null;
      uidSeq++;
      if (!main.id) main.id = 'vi-knowledge-area-main-' + uidSeq;
      var title = head.querySelector('.vi-knowledge__section-title');
      if (title && !title.id) title.id = 'vi-knowledge-area-title-' + uidSeq;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'vi-knowledge__area-toggle';
      btn.hidden = true; // visas endast i mobil-läget (applyMode)
      btn.setAttribute('aria-expanded', 'true');
      btn.setAttribute('aria-controls', main.id);
      if (title) btn.setAttribute('aria-labelledby', title.id);
      else btn.setAttribute('aria-label', 'Visa eller dölj avsnittet');
      var icon = document.createElement('span');
      icon.className = 'vi-knowledge__area-toggle-icon';
      icon.setAttribute('aria-hidden', 'true');
      btn.appendChild(icon);

      // Antal-chip: ren visuell spegel av motorns count (aria-hidden så
      // siffran inte läses dubbelt — originalet har aria-live).
      var chip = document.createElement('span');
      chip.className = 'vi-knowledge__area-toggle-count';
      chip.hidden = true;
      chip.setAttribute('aria-hidden', 'true');
      if (title && title.nextSibling) head.insertBefore(chip, title.nextSibling);
      else head.appendChild(chip);
      head.appendChild(btn);

      var entry = { area: area, btn: btn, chip: chip, countEl: areaCountSource(area) };

      btn.addEventListener('click', function () {
        if (!isMobile()) return;
        setExpanded(entry, btn.getAttribute('aria-expanded') !== 'true');
        syncChip(entry);
      });

      // Counts fylls asynkront (motorns fetch / kartans fetch) — spegla dem
      // löpande utan att röra motorn.
      if (typeof window.MutationObserver === 'function' && entry.countEl) {
        try {
          new window.MutationObserver(function () { syncChip(entry); })
            .observe(entry.countEl, { childList: true, characterData: true, subtree: true });
        } catch (e) { /* icke-fatal — chipet synkas ändå vid ready/klick */ }
      }
      return entry;
    }

    function applyMode(mobile) {
      for (var i = 0; i < enhanced.length; i++) {
        var rec = enhanced[i];
        if (rec.root && rec.root.isConnected === false) continue; // utbytt i temaeditorn
        for (var j = 0; j < rec.areas.length; j++) {
          var entry = rec.areas[j];
          if (entry.btn) entry.btn.hidden = !mobile;
          if (entry.chip) entry.chip.hidden = !mobile;
          if (mobile) syncChip(entry);
          // Mobil: startläge = allt hopfällt. Desktop: allt expanderat och
          // mobilklasserna borttagna (kravet vid resize över gränsen).
          setExpanded(entry, !mobile);
        }
      }
    }

    function enhanceRoot(root) {
      if (!root || root.getAttribute('data-vi-mobile-compact') === '1') return;
      root.setAttribute('data-vi-mobile-compact', '1');
      var nodes = root.querySelectorAll('.vi-knowledge__area[data-vi-area-key]');
      var areas = [];
      for (var i = 0; i < nodes.length; i++) {
        var entry = buildToggle(nodes[i]);
        if (entry) areas.push(entry);
      }
      if (!areas.length) return;
      var rec = { root: root, areas: areas };
      enhanced.push(rec);

      // Kategorikort ([data-vi-cat]) → auto-expandera målområdet på mobil.
      // Extra lyssnare; motorns egen delegation (filter + scroll) är orörd.
      root.addEventListener('click', function (ev) {
        if (!isMobile()) return;
        var t = ev.target && ev.target.closest ? ev.target.closest('[data-vi-cat]') : null;
        if (!t || !root.contains(t)) return;
        var key = t.getAttribute('data-vi-cat');
        if (!key) return;
        for (var j = 0; j < rec.areas.length; j++) {
          if (rec.areas[j].area.getAttribute('data-vi-area-key') === key) {
            setExpanded(rec.areas[j], true);
            syncChip(rec.areas[j]);
          }
        }
      });

      // När motorn är klar finns riktiga counts — synka chipen.
      root.addEventListener(NS + ':ready', function () {
        for (var j = 0; j < rec.areas.length; j++) syncChip(rec.areas[j]);
      });

      // Initialt läge för denna rot.
      var mobile = isMobile();
      for (var k = 0; k < areas.length; k++) {
        if (areas[k].btn) areas[k].btn.hidden = !mobile;
        if (areas[k].chip) areas[k].chip.hidden = !mobile;
        if (mobile) {
          setExpanded(areas[k], false);
          syncChip(areas[k]);
        }
      }
    }

    function bootMobile() {
      var roots = document.querySelectorAll('[data-vi-knowledge]');
      for (var i = 0; i < roots.length; i++) enhanceRoot(roots[i]);
    }

    function onModeChange(ev) {
      applyMode(!!(ev && typeof ev.matches === 'boolean' ? ev.matches : mql.matches));
    }
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onModeChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onModeChange);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootMobile);
    } else {
      bootMobile();
    }
    document.addEventListener('shopify:section:load', function (ev) {
      if (!ev || !ev.target || !ev.target.querySelectorAll) return;
      var roots = ev.target.querySelectorAll('[data-vi-knowledge]');
      for (var i = 0; i < roots.length; i++) enhanceRoot(roots[i]);
    });
  })();
})();
